import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DeepTodo } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

import { CheckCircle2, Circle, Loader2, NotebookPen } from "@/components/icons"
import { cn } from "@/lib/utils"

// ── Live timer helpers ────────────────────────────────────────────────────────

function getElapsedSeconds(startedAt: string | null | undefined): number {
  if (!startedAt) return 0
  return Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 1000))
}

function formatElapsed(secs: number): string {
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const rem = secs % 60
  if (mins < 60) return `${mins}m ${rem}s`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  return `${hours}h ${remMins}m`
}

function getDoneElapsed(todo: DeepTodo): string | null {
  if (!todo.started_at || !todo.completed_at) return null
  const secs = Math.max(
    0,
    Math.round((new Date(todo.completed_at).getTime() - new Date(todo.started_at).getTime()) / 1000)
  )
  return formatElapsed(secs)
}

// ── Components ────────────────────────────────────────────────────────────────

interface TodoRowProps {
  todo: DeepTodo
  /** Live elapsed seconds for in_progress todos (from parent ticker) */
  elapsedMap: Record<string, number>
  t: TFunction
}

function TodoRow({ todo, elapsedMap, t }: TodoRowProps) {
  const isInProgress = todo.status === "in_progress"
  const isPending = todo.status === "pending"
  const isDone = todo.status === "completed"

  const elapsed = isInProgress ? (elapsedMap[todo.id] ?? 0) : null
  const doneTime = isDone ? getDoneElapsed(todo) : null

  return (
    <li
      aria-busy={isInProgress}
      aria-label={
        isInProgress && elapsed !== null
          ? t("deepExplorer.todo_in_progress_aria", {
              task: todo.content || todo.text,
              elapsed: formatElapsed(elapsed),
            })
          : todo.content || todo.text
      }
      className={cn(
        "flex items-start gap-2 py-1.5",
        isPending && "opacity-60",
        isDone && "opacity-70"
      )}
    >
      <span className="mt-0.5 shrink-0">
        {isPending && <Circle className="text-muted-foreground size-3.5" />}
        {isInProgress && (
          <Loader2 className="text-primary size-3.5 animate-spin motion-reduce:animate-none" />
        )}
        {isDone && <CheckCircle2 className="text-success size-3.5" />}
      </span>
      <span className="min-w-0 flex-1 text-sm leading-snug">{todo.content || todo.text}</span>
      {isInProgress && elapsed !== null && (
        <span
          aria-hidden="true"
          className="text-primary ml-2 shrink-0 text-right text-xs tabular-nums"
        >
          {formatElapsed(elapsed)}
        </span>
      )}
      {isDone && doneTime && (
        <span className="text-muted-foreground ml-2 shrink-0 text-right text-xs tabular-nums">
          {doneTime}
        </span>
      )}
    </li>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

interface TodoListProps {
  todos: DeepTodo[]
}

export function TodoList({ todos }: TodoListProps) {
  const { t } = useTranslation()
  // Live elapsed map: { todoId → elapsed seconds }
  const [elapsedMap, setElapsedMap] = useState<Record<string, number>>({})
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Keep a ref to todos so the interval callback always reads the latest
  const todosRef = useRef(todos)
  todosRef.current = todos

  const doneCount = todos.filter((t) => t.status === "completed").length
  const totalCount = todos.length
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0

  // Stable key: sorted IDs of in-progress todos.  The interval only needs to
  // be recreated when the SET of in-progress items changes, not on every
  // Zustand state replacement (which gives a new array reference even when
  // contents are identical).
  const inProgressKey = todos
    .filter((t) => t.status === "in_progress")
    .map((t) => t.id)
    .sort()
    .join(",")

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (!inProgressKey) {
      return
    }

    // Initialize elapsed map immediately
    const buildMap = () => {
      const map: Record<string, number> = {}
      for (const todo of todosRef.current) {
        if (todo.status === "in_progress") {
          map[todo.id] = getElapsedSeconds(todo.started_at)
        }
      }
      return map
    }

    setElapsedMap(buildMap())

    intervalRef.current = setInterval(() => {
      // Skip tick when tab is hidden (battery savings)
      if (document.visibilityState === "hidden") return
      setElapsedMap(buildMap())
    }, 1000)

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [inProgressKey])

  if (todos.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <NotebookPen className="text-muted-foreground/60 size-8" />
        <p className="text-muted-foreground text-sm">{t("deepExplorer.no_plan_yet")}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Progress bar */}
      <div className="flex items-center gap-2">
        <div className="bg-muted h-2 flex-1 overflow-hidden rounded-full">
          <div
            className="bg-primary-ink h-full rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
          {doneCount}/{totalCount}
        </span>
      </div>
      <ol
        aria-label={t("deepExplorer.agent_plan_aria")}
        aria-live="polite"
        className="flex flex-col gap-0"
      >
        {todos.map((todo, index) => (
          <TodoRow key={todo.id || `todo-${index}`} elapsedMap={elapsedMap} t={t} todo={todo} />
        ))}
      </ol>
    </div>
  )
}
